<template>
	<div
		v-if="internalIsMobileLandscape"
		class="multi-format-tool__rotate-message"
	>
		<Icon
			icon-name="restore-1"
			size="xxl"
		/>
		{{ $I18n.trans('commons.rotate_device') }}
	</div>
	<div
		v-else
		class="multi-format-tool"
		:class="[
			`is-${currentUiStatus}`,
			{ 'is-community-cropping': isCommunityCropping },
			{ 'is-mobile': internalIsMobile },
		]"
	>
		<ModalKeyboardShortcuts
			v-if="isCommunityCropping"
			:modal-name="modalNameKeyboardShortcuts"
		/>
		<div
			v-if="isCommunityCropping"
			class="multi-format-tool__header"
		>
			<div class="multi-format-tool__title">
				<span>Clip your moment</span>
			</div>
			<div class="multi-format-tool__header-right">
				<Button
					v-if="!internalIsMobile"
					class="multi-format-tool__help-button"
					size="medium"
					theme="dark"
					modifier="borderless"
					text="?"
					@click="openHelp"
				/>
			</div>
		</div>
		<div
			v-scroll-container="{
				target: '.js-scroll-wrapper',
				disableScrollShadow: false,
			}"
			class="multi-format-tool__main-content"
		>
			<div
				v-adaptive-height="{ target: '.js-multi-format-tool-player' }"
				class="multi-format-tool__col -main"
			>
				<div class="multi-format-tool__tool-w l-asport-one-panel-section">
					<div class="multi-format-tool__main-content">
						<Modal
							:modal-name="modalNameDownloadVideo"
							type="dialog"
							:theme="$arenaConfig.isLightTheme ? null : 'dark'"
						>
							<template #title>
								{{ $I18n.trans('commons.type_create', { type: $I18n.trans_choice('commons.video', 1)}) }}
							</template>
							<template #content>
								<div v-if="currentUiStatus === UI_STATUS.EMAIL_NOTIFICATION_REQUESTED">
									<div
										class="multi-format-tool__notification-message"
									>
										<Icon
											icon-name="fireworks"
											size="xl"
										/>
										{{ $I18n.trans('event.multiformat_editor.messages.notification_success') }}
									</div>
								</div>
								<div
									v-else-if="currentUiStatus === UI_STATUS.GENERATING_VIDEO_CROP || currentUiStatus === UI_STATUS.VIDEO_CROP_OVERTIME"
									class="multi-format-tool__in-progress-info"
								>
									<LoadingSpinner
										color="light"
										:theme="$arenaConfig.isLightTheme ? 'dark' : 'light'"
										class="multi-format-tool__loading-spinner"
									/>
									{{ $I18n.trans('analysis.video_export.progress.create_video') }}
									<div
										v-if="currentUiStatus === UI_STATUS.VIDEO_CROP_OVERTIME"
										class="multi-format-tool__overtime-message"
									>
										{{ $I18n.trans('event.multiformat_editor.messages.processing_overtime') }}
										<InputText
											ref="emailInput"
											:dark-theme="!$arenaConfig.isLightTheme"
											label="E-Mail"
											type="email"
											class="multi-format-tool__overtime-email"
											:value="email"
											@input="(value) => email = value"
										/>
									</div>
									<div
										v-if="emailNotificationError"
										class="multi-format-tool__notification-message"
									>
										<Icon
											icon-name="sad"
											size="xl"
										/>
										{{ $I18n.trans('event.multiformat_editor.messages.notification_error') }}
									</div>
								</div>
								<template v-if="currentUiStatus === UI_STATUS.VIDEO_CROP_AVAILABLE">
									{{ $I18n.trans('event.multiformat_editor.messages.video_created') }}
									<InputCopy
										v-if="cropVideoDownloadUrl"
										class="multi-format-tool__copy-link"
										:label="$I18n.trans('event.multiformat_editor.labels.copy_link', {days: `14 ${$I18n.trans_choice('commons.day', 14)}`})"
										:value="cropVideoDownloadUrl"
										:no-wrap="true"
										:dark-theme="!$arenaConfig.isLightTheme"
									/>
								</template>
							</template>
							<template #footer>
								<div class="l-button-group">
									<template v-if="currentUiStatus === UI_STATUS.EMAIL_NOTIFICATION_REQUESTED">
										<Button
											:text="$Helpers.ucfirst($I18n.trans('commons.close'))"
											modifier="secondary"
											theme="dark"
											@click="closeDownloadModal"
										/>
									</template>
									<template v-else>
										<Button
											v-if="currentUiStatus !== UI_STATUS.VIDEO_CROP_AVAILABLE"
											:text="$Helpers.ucfirst($I18n.trans('commons.cancel'))"
											modifier="secondary"
											theme="dark"
											@click="closeDownloadModal"
										/>
										<Button
											v-if="currentUiStatus === UI_STATUS.VIDEO_CROP_OVERTIME && !emailNotificationSaved"
											:text="$Helpers.ucfirst($I18n.trans('commons.save'))"
											:disabled="!($refs.emailInput && $refs.emailInput.isValid)"
											theme="dark"
											@click="storeEmailNotification"
										/>
										<Button
											v-if="isShareButtonVisible"
											:text="$Helpers.ucfirst($I18n.trans('commons.type_share', { type: $I18n.trans_choice('commons.video', 1)}))"
											modifier="secondary"
											theme="dark"
											@click="shareVideo"
										/>
										<Button
											v-if="currentUiStatus === UI_STATUS.VIDEO_CROP_AVAILABLE"
											:text="$Helpers.ucfirst($I18n.trans('commons.download'))"
											theme="dark"
											@click="downloadCropVideo"
										/>
									</template>
								</div>
							</template>
						</Modal>

						<div
							ref="playerWrapper"
							class="multi-format-tool__player-w"
						>
							<MultiFormatPlayer
								ref="cropperVideo"
								:hls-url="hlsUrl"
								:ui-status="currentUiStatus"
								:show-controls="showControls"
								:tag-time="internalTagTime"
								:internal-is-mobile-portrait="internalIsMobilePortrait"
								@coordinates-update="coordinatesUpdate"
								@cropstart="handleCropStart"
								@video-ended="handleVideoEnded"
								@duration-update="setMaxDuration"
							/>
						</div>

						<template v-if="currentUiStatus === UI_STATUS.PREVIEW">
							<div
								ref="previewContainer"
								class="multi-format-tool__preview-container"
								:style="maskStyle"
							/>
							<div
								v-if="selectedGraphic"
								class="multi-format-tool__preview-graphic-container"
								:style="{width: `${cropPosition.width + 2}px`}"
							>
								<div class="multi-format-tool__preview-graphic">
									<img
										:src="selectedGraphic.imageUrl"
										class="multi-format-tool__preview-graphic-image"
									>
								</div>
							</div>
						</template>
					</div>
				</div>
				<div
					v-if="!internalIsMobile"
					class="multi-format-tool__actions-w l-asport-one-panel-section"
				>
					<MultiFormatControls
						:current-ui-status="currentUiStatus"
						:max-duration-reached-info-visible="maxDurationReachedInfoVisible"
						:remaining-clip-time-secs="remainingClipTimeSecs"
						:remaining-clip-time-secs-leading-zero="remainingClipTimeSecsLeadingZero"
						:preview-countdown="previewCountdown"
						:preview-countdown-leading-zero="previewCountdownLeadingZero"
						:is-community-cropping="isCommunityCropping"
						:selected-graphic-id="selectedGraphicId"
						:graphic-options="graphicOptions"
						@setReadyStatus="setReadyStatus"
						@stopClipRecording="stopClipRecording"
						@cancelReadyStatus="cancelReadyStatus"
						@previewClip="previewClip"
						@resetClipping="resetClipping"
						@stopPreview="stopPreview"
						@createVideoCrop="createVideoCrop"
						@setSelectedGraphicId="selectedGraphicId = $event"
					/>
				</div>
			</div>
			<div
				v-if="!internalIsMobile"
				class="multi-format-tool__col -right"
			>
				<div
					class="multi-format-tool__clips-w l-asport-one-panel-section -no-padding"
				>
					<ClipList
						:hide-context-menu="true"
						:hide-likes="true"
						:hide-rating="true"
						:hide-share="true"
						:hide-filter="true"
						:hide-sort="true"
						default-sort="asc"
						:disabled="currentUiStatus !== UI_STATUS.INITIAL"
						@play="handleTagPlay"
					/>
				</div>
			</div>

			<Tab
				v-if="internalIsMobile"
				icon-size="lg"
				class="multi-format-tool__tab-nav"
				:active-tab="activeTab"
				:tabs="tabs"
				@tab-change="handleTabChange"
			>
				<!-- controls -->
				<template #tab-details-controls>
					<MultiFormatControls
						class="multi-format-tool__controls"
						:current-ui-status="currentUiStatus"
						:max-duration-reached-info-visible="maxDurationReachedInfoVisible"
						:remaining-clip-time-secs="remainingClipTimeSecs"
						:remaining-clip-time-secs-leading-zero="remainingClipTimeSecsLeadingZero"
						:preview-countdown="previewCountdown"
						:preview-countdown-leading-zero="previewCountdownLeadingZero"
						:is-community-cropping="isCommunityCropping"
						:selected-graphic-id="selectedGraphicId"
						:graphic-options="graphicOptions"
						@setReadyStatus="setReadyStatus"
						@stopClipRecording="stopClipRecording"
						@cancelReadyStatus="cancelReadyStatus"
						@previewClip="previewClip"
						@resetClipping="resetClipping"
						@stopPreview="stopPreview"
						@createVideoCrop="createVideoCrop"
						@setSelectedGraphicId="selectedGraphicId = $event"
					/>
				</template>
				<!-- clips -->
				<template #tab-details-clips>
					<ClipList
						class="multi-format-tool__clips l-asport-one-panel-section -no-padding"
						:hide-context-menu="true"
						:hide-likes="true"
						:hide-rating="true"
						:hide-share="true"
						:hide-filter="true"
						:hide-sort="true"
						default-sort="asc"
						@play="handleTagPlay"
					/>
				</template>
			</Tab>
		</div>
	</div>
</template>

<script>
import MultiFormatPlayer from '@shared/components/MultiFormatTool/MultiFormatPlayer';
import Button from '@shared/components/Button';
import Icon from '@shared/components/Icon';
import Modal from '@shared/components/Modal';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import InputText from '@shared/components/Form/InputText';
import InputCopy from '@shared/components/Form/InputCopy';
import ClipList from '@videotools/js/components/clip-list/ClipList';
import MultiFormatControls from '@shared/components/MultiFormatTool/MultiFormatControls';
import ModalKeyboardShortcuts from '@shared/components/ModalKeyboardShortcuts.vue';
import Tab from '@components/tab';
import laroute from '@laroute';
import * as Sentry from '@sentry/vue';
import { api } from '../../../manager/js/util/api.js';
import ws from '@shared/js/websocket/websocket-client.js';
import { WS_EVENT } from '@shared/js/websocket/constants.js';
import { getShortLinkForMoment } from '@shared/utils/getShortLink.js';
import { GraphicAspectRatio } from '../../enums/GraphicAspectRatio.js';
import { leadingZero } from '@shared/utils/formatNumber.js';
import VScrollContainer from '@shared/directives/v-scroll-container';
import AdaptiveHeight from '@shared/directives/v-adaptive-height';

const CLIP_INTERVAL_MS = 1000;
const API_POLLING_INTERVAL_MS = 2000;
const CROP_VIDEO_PROCESSING_OVERTIME = 60000; // 1 minute
const CLIP_MAX_DURATION_MS = 30000;

export const UI_STATUS = {
	INITIAL:                      'initial',
	READY:                        'ready',
	RECORDING:                    'recording',
	FINISHED_RECORDING:           'finished-recording',
	PREVIEW:                      'preview',
	GENERATING_VIDEO_CROP:        'generating-video-crop',
	VIDEO_CROP_AVAILABLE:         'video-crop-available',
	VIDEO_CROP_FAILURE:           'video-crop-failure',
	VIDEO_CROP_OVERTIME:          'video-crop-overtime',
	EMAIL_NOTIFICATION_REQUESTED: 'email-notification-requested',
};

export const VIDEO_CROP_STATUS = {
	JOB_CREATED: 'created',
	PROCESSING:  'processing',
	AVAILABLE:   'available',
	FAILED:      'failed',
};

export const KEYBOARD_SHORTCUTS = [
	{
		key:         ' ',
		description: $I18n.trans('analysis.keyboard_shortcuts.keys.capture:spacebar'),
		shiftKey:    false,
	},
	{
		key:         'ArrowLeft',
		description: $I18n.trans('analysis.keyboard_shortcuts.keys.hotkey:seek-backwards-one-sec'),
		shiftKey:    false,
	},
	{
		key:         'ArrowRight',
		description: $I18n.trans('analysis.keyboard_shortcuts.keys.hotkey:seek-forwards-one-sec'),
		shiftKey:    false,
	},
	{
		key:         'Escape',
		description: $I18n.trans('analysis.keyboard_shortcuts.keys.capture:escape'),
		shiftKey:    false,
	},
];

const getMobileQuery = () => {
	return window.matchMedia('(max-width: 1024px)').matches && window.matchMedia('(pointer: coarse)').matches;
};
const getPortraitQuery = () => {
	return window.matchMedia('(orientation: portrait)').matches;
};
const getLandscapeQuery = () => {
	return window.matchMedia('(orientation: landscape)').matches;
};

export default {
	name:       'MultiFormatTool',
	components: { MultiFormatPlayer, Button, Modal, LoadingSpinner, InputText, Icon, InputCopy, ClipList, MultiFormatControls, Tab, ModalKeyboardShortcuts },
	directives: {
		scrollContainer: VScrollContainer,
		adaptiveHeight:  AdaptiveHeight,
	},
	props: {
		eventId: {
			type:     Number,
			required: true,
		},
		hlsUrl: {
			type:    String,
			default: null,
		},
		tagTime: {
			type:    Object,
			default: null,
		},
		graphicSets: {
			type:    Array,
			default: () => [],
		},
		defaultGraphicSet: {
			type:    Object,
			default: null,
		},
		isCommunityCropping: {
			type:    Boolean,
			default: false,
		},
		uiStatus: {
			type:    String,
			default: null,
		},
	},
	data() {
		return {
			cropper:          null,
			clipUrl:          null,
			email:            null,
			elapsedClipTime:  0,
			previewCountdown: 0,
			clipData:         {
				keyPoints:   [],
				startTime:   0,
				duration:    0,
				aspectRatio: GraphicAspectRatio.AR_9_16,
			},
			maxClipDuration:          CLIP_MAX_DURATION_MS,
			clipInterval:             null,
			cropOvertimeInterval:     null,
			checkVideoStatusInterval: null,
			cropTaskWsSubscription:   null,
			cropVideoId:              null,
			cropVideoDataUrl:         null,
			cropVideoDownloadUrl:     null,
			cropVideoStatus:          null,
			cropVideoStart:           null,
			selectedGraphicId:        null,
			cropPosition:             {
				x:     0,
				width: 0,
			},
			maxDurationReachedInfoVisible: false,
			emailNotificationSaved:        false,
			emailNotificationError:        false,
			modalNameDownloadVideo:        'download-video',
			internalUiStatus:              UI_STATUS.INITIAL,
			UI_STATUS,
			internalIsMobile:              getMobileQuery(),
			internalIsPortrait:            getPortraitQuery(),
			internalIsLandscape:           getLandscapeQuery(),
			videoReadyForSharing:          false,
			videoBlob:                     null,
			activeTab:                     'controls',
			internalTagTime:               this.tagTime,
			modalNameKeyboardShortcuts:    'ModalKeyboardShortcuts',
		};
	},
	computed: {
		previewCountdownLeadingZero() {
			return leadingZero(this.previewCountdown);
		},
		remainingClipTimeSecsLeadingZero() {
			return leadingZero(this.remainingClipTimeSecs);
		},
		currentUiStatus() {
			// Use the prop value if provided, otherwise use internal state
			return this.uiStatus || this.internalUiStatus;
		},
		showControls() {
			return ![UI_STATUS.READY, UI_STATUS.PREVIEW, UI_STATUS.RECORDING, UI_STATUS.FINISHED_RECORDING].includes(this.currentUiStatus);
		},
		internalIsMobilePortrait() {
			return this.internalIsMobile && this.internalIsPortrait;
		},
		internalIsMobileLandscape() {
			return this.internalIsMobile && this.internalIsLandscape;
		},
		remainingClipTimeSecs() {
			return (this.maxClipDuration / 1000) - (Math.abs(Math.round(this.maxClipDuration - this.elapsedClipTime) / 1000)) - 1; // -1 to show 0 at the beginning
		},
		controlsWrapperPositionStyle() {
			return {
				width:     `${this.cropPosition.width}px`,
				transform: `translateX(${this.cropPosition.x}px)`,
			};
		},
		maskStyle() {
			return {
				maskImage: `linear-gradient(to right,
			black 0%,
			black calc(50% - ${Math.floor(this.cropPosition.width / 2)}px),
			transparent calc(50% - ${this.cropPosition.width / 2}px),
			transparent calc(50% + ${this.cropPosition.width / 2}px),
			black calc(50% + ${Math.floor(this.cropPosition.width / 2)}px),
			black 100%)`,
			};
		},
		selectedGraphic() {
			return this.graphicOptions.find(graphic => graphic.value === this.selectedGraphicId);
		},
		graphicOptions() {
			let sets = this.graphicSets;
			const options = [];

			// Add an option for no logo on non-community crops
			if (!this.defaultGraphicSet && !this.isCommunityCropping) {
				options.push({ label: $I18n.trans('event.multiformat_editor.labels.no_logo'), value: null });
			}

			if (this.isCommunityCropping && this.defaultGraphicSet) {
				sets = [this.defaultGraphicSet];
			}

			sets.forEach(set => {
				const validGraphics = set.graphics.filter(graphic => [this.clipData.aspectRatio].includes(graphic.aspectRatio));
				validGraphics.forEach(graphic => {
					options.push({
						label:    `${set.name} - ${graphic.aspectRatio}`,
						value:    graphic.id,
						imageUrl: graphic.imageUrl,
					});
				});
			});

			return options;
		},
		isFileSharingSupported() {
			return navigator.share && navigator.canShare;
		},
		isShareButtonVisible() {
			return this.currentUiStatus === UI_STATUS.VIDEO_CROP_AVAILABLE &&
				this.isFileSharingSupported &&
				this.isCommunityCropping &&
				this.videoReadyForSharing;
		},
		tabs() {
			return [
				{
					key:  'controls',
					icon: 'bullet-list',
				},
				{
					key:  'clips',
					icon: 'video-playlist',
				},
			];
		},
	},
	watch: {
		cropVideoStatus(value) {
			if (value === VIDEO_CROP_STATUS.JOB_CREATED) {
				this.internalUiStatus = UI_STATUS.GENERATING_VIDEO_CROP;
				this.eventBus.$emit(`modal:${this.modalNameDownloadVideo}`);
			}
			if (value === VIDEO_CROP_STATUS.PROCESSING) {
				this.internalUiStatus = UI_STATUS.GENERATING_VIDEO_CROP;
			}
			if (value === VIDEO_CROP_STATUS.AVAILABLE) {
				this.internalUiStatus = UI_STATUS.VIDEO_CROP_AVAILABLE;
			}
			if (value === VIDEO_CROP_STATUS.FAILED) {
				this.internalUiStatus = UI_STATUS.VIDEO_CROP_FAILURE;
				this.eventBus.$emit(`modalClose:${this.modalNameDownloadVideo}`);
				this.$toast.error(this.$I18n.trans('commons.something_went_wrong'));
			}

			if ([VIDEO_CROP_STATUS.AVAILABLE, VIDEO_CROP_STATUS.FAILED].includes(value)) {
				if (this.checkVideoStatusInterval) {
					clearInterval(this.checkVideoStatusInterval);
				}
				if (this.cropOvertimeInterval) {
					clearInterval(this.cropOvertimeInterval);
				}
			}
		},
	},
	mounted() {
		this.handleKeybindings();
		this.handleOrientationChange();
		this.setDefaultGraphic();
	},
	beforeDestroy() {
		// Remove event listeners to prevent memory leaks
		document.removeEventListener('keydown', this.handleKeydown);
		window.matchMedia('(orientation: portrait)').removeEventListener('change', this.handleOrientationChangeEvent);
		window.matchMedia('(max-width: 1024px)').removeEventListener('change', this.handleOrientationChangeEvent);
	},
	methods: {
		handleKeybindings() {
			document.addEventListener('keydown', this.handleKeydown);
		},
		setDefaultGraphic() {
			this.selectedGraphicId = this.graphicOptions[0]?.value || null;
		},
		handleKeydown(event) {
			if (event.key === ' ') {
				if (this.currentUiStatus === UI_STATUS.INITIAL) {
					this.setReadyStatus();
				} else if (this.currentUiStatus === UI_STATUS.READY) {
					this.startClipRecording();
				} else if (this.currentUiStatus === UI_STATUS.RECORDING) {
					this.stopClipRecording();
				}
			}

			if (event.key === 'ArrowLeft') {
				if (this.currentUiStatus === UI_STATUS.INITIAL) {
					this.$refs.cropperVideo.seek(-1);
				}
			}

			if (event.key === 'ArrowRight') {
				if (this.currentUiStatus === UI_STATUS.INITIAL) {
					this.$refs.cropperVideo.seek(1);
				}
			}

			if (event.key === 'Escape') {
				if ([UI_STATUS.RECORDING, UI_STATUS.READY].includes(this.currentUiStatus)) {
					this.cancelReadyStatus();
				}
			}
		},
		handleOrientationChange() {
			window.matchMedia('(orientation: portrait)').addEventListener('change', this.handleOrientationChangeEvent);
			window.matchMedia('(max-width: 1024px)').addEventListener('change', this.handleOrientationChangeEvent);
		},
		handleOrientationChangeEvent(_event) {
			this.internalIsMobile = getMobileQuery();
			this.internalIsPortrait = getPortraitQuery();
			this.internalIsLandscape = getLandscapeQuery();
		},
		coordinatesUpdate(data) {
			window.requestAnimationFrame(() => {
				this.cropPosition.width = data.width + 2;
				this.cropPosition.x = data.left + 2;
			});
		},
		handleCropStart() {
			if (this.currentUiStatus === UI_STATUS.READY) {
				this.startClipRecording();
			}
		},
		setPlayerClippingMode(value) {
			if (value) {
				this.$refs.cropperVideo.play();
			} else {
				this.$refs.cropperVideo.pause();
			}
		},
		async resetClipping() {
			await this.$refs.cropperVideo.setVideoTime(this.clipData.startTime ?? 0);
			this.clipData = {
				keyPoints:   [],
				startTime:   0,
				duration:    0,
				aspectRatio: GraphicAspectRatio.AR_9_16,
			};
			this.elapsedClipTime = 0;
			this.internalUiStatus = UI_STATUS.INITIAL;
			this.emailNotificationSaved = false;
			this.emailNotificationError = false;
			this.email = null;
			this.cropVideoId = null;
			this.cropVideoDataUrl = null;
			this.cropVideoDownloadUrl = null;
			this.cropVideoStatus = null;
			this.setPlayerClippingMode(false);
		},
		setReadyStatus() {
			this.internalUiStatus = UI_STATUS.READY;
		},
		cancelReadyStatus() {
			this.internalUiStatus = UI_STATUS.INITIAL;
			this.setPlayerClippingMode(false);
			this.resetClipping();

			if (this.clipInterval) {
				clearInterval(this.clipInterval);
				this.elapsedClipTime = 0;
			}
		},
		handleVideoEnded() {
			if (this.currentUiStatus === UI_STATUS.RECORDING) {
				this.stopClipRecording();
			}
		},
		startClipRecording() {
			this.internalUiStatus = UI_STATUS.RECORDING;

			// Set player to play
			this.setPlayerClippingMode(true);

			this.clipData.startTime = this.$refs?.cropperVideo.position;

			// Save initial position
			this.saveClipData();
			this.elapsedClipTime += CLIP_INTERVAL_MS;

			this.clipInterval = setInterval(() => {
				if (this.elapsedClipTime >= this.maxClipDuration) {
					this.showMaxDurationReachedInfo();
					this.stopClipRecording();
					return;
				}
				this.saveClipData();
				this.elapsedClipTime += CLIP_INTERVAL_MS;
			}, CLIP_INTERVAL_MS);
		},
		stopClipRecording() {
			this.internalUiStatus = UI_STATUS.FINISHED_RECORDING;
			this.clipData.duration = Math.abs((this.elapsedClipTime / 1000));
			this.setPlayerClippingMode(false);
			if (this.clipInterval) {
				clearInterval(this.clipInterval);
				this.elapsedClipTime = 0;
			}
		},
		async previewClip() {
			this.internalUiStatus = UI_STATUS.PREVIEW;

			await this.$refs.cropperVideo.setVideoTime(this.internalTagTime ? this.clipData.startTime - this.internalTagTime.start : this.clipData.startTime);
			await this.$refs.cropperVideo.play();
			this.$refs.playerWrapper.style.transition = '';
			// set initial position to first key, then set position to second key to start transition which will end after 1s
			this.$refs.playerWrapper.style.transform = `translateX(calc(50% - ${this.cropPosition.width / 2}px - ${this.clipData.keyPoints[0].xLeft * 100}%))`;
			window.requestAnimationFrame(() => {
				this.$refs.playerWrapper.style.transition = 'transform 1s linear';
				this.$refs.playerWrapper.style.transform = `translateX(calc(50% - ${this.cropPosition.width / 2}px - ${this.clipData.keyPoints[1].xLeft * 100}%))`;
			});

			this.previewCountdown = this.clipData.duration;

			let counter = 2;

			this.previewInterval = setInterval(() => {
				this.previewCountdown -= 1;
				if (counter >= this.clipData.keyPoints.length) {
					// wait for the last transition to finish
					setTimeout(() => {
						this.stopPreview();
					}, 1000);
					return;
				}
				this.$refs.playerWrapper.style.transform = `translateX(calc(50% - ${this.cropPosition.width / 2}px - ${this.clipData.keyPoints[counter].xLeft * 100}%))`;
				counter += 1;
			}, CLIP_INTERVAL_MS);
		},
		stopPreview() {
			this.internalUiStatus = UI_STATUS.FINISHED_RECORDING;
			this.$refs.cropperVideo.pause();
			this.$refs.playerWrapper.style.transition = '';
			this.$refs.playerWrapper.style.transform = 'translateX(0)';
			clearInterval(this.previewInterval);
		},
		async handleTagPlay(clip) {
			this.internalTagTime = null;
			const start = Math.max(0, clip.timestamp - clip.secondsBefore);
			await this.$refs.cropperVideo.setVideoTime(start);
			this.$refs.cropperVideo.play();
		},
		saveClipData() {
			const cropperRef = this.$refs?.cropperVideo;
			const cropBoxData = cropperRef?.cropper?.getCropBoxData();
			this.clipData.keyPoints.push({
				t:       (this.elapsedClipTime / CLIP_INTERVAL_MS) ?? 0,
				yTop:    (cropBoxData?.top ?? 0) / (cropperRef?.getCropContainerSize().height ?? 0), // Percentage top offset
				xLeft:   (cropBoxData?.left ?? 0) / (cropperRef?.getCropContainerSize().width ?? 0), // Percentage left offset
				xWidth:  (cropBoxData?.width ?? 0) / (cropperRef?.getCropContainerSize().width ?? 0), // Percentage width
				yHeight: (cropBoxData?.height ?? 0) / (cropperRef?.getCropContainerSize().height ?? 0), // Percentage height
			});
		},
		downloadCropVideo() {
			window.open(this.cropVideoDownloadUrl, '_blank');
		},
		closeDownloadModal() {
			if (this.cropTaskWsSubscription) {
				ws.unsubscribe(this.cropTaskWsSubscription);
			}
			if (this.checkVideoStatusInterval) {
				clearInterval(this.checkVideoStatusInterval);
			}
			this.resetClipping();
			this.eventBus.$emit(`modalClose:${this.modalNameDownloadVideo}`);
		},
		showMaxDurationReachedInfo() {
			this.maxDurationReachedInfoVisible = true;
			setTimeout(() => {
				this.maxDurationReachedInfoVisible = false;
			}, 5000);
		},
		async getVideoCropStatus() {
			this.cropVideoStart = this.cropVideoStart || Date.now();

			const response = await fetch(this.cropVideoDataUrl);
			const data = await response.json();

			// For testing purposes
			if (new URLSearchParams(window.location.search).has('slow')) {
				this.cropVideoStatus = VIDEO_CROP_STATUS.PROCESSING;
			} else {
				this.cropVideoStatus = data.status;
			}

			// Get a shortlink for the video crop
			if (this.cropVideoStatus === VIDEO_CROP_STATUS.AVAILABLE) {
				this.cropVideoDownloadUrl = await getShortLinkForMoment(this.cropVideoId);
				this.prefetchVideoForSharing();
			}

			// Keep checking crop until failed or processed
			if ([VIDEO_CROP_STATUS.AVAILABLE, VIDEO_CROP_STATUS.FAILED].includes(this.cropVideoStatus)) {
				clearInterval(this.checkVideoStatusInterval);
			}

			// Video takes longer to process than expected
			const timeElapsed = Date.now() - this.cropVideoStart;
			if (timeElapsed > CROP_VIDEO_PROCESSING_OVERTIME) {
				this.internalUiStatus = UI_STATUS.VIDEO_CROP_OVERTIME;
			}
		},
		async createVideoCrop() {
			try {
				if (!this.isCommunityCropping) {
					this.clipData.graphicId = this.selectedGraphicId;
				}
				this.internalUiStatus = UI_STATUS.GENERATING_VIDEO_CROP;

				const headers = {
					'Content-Type': 'application/json',
				};

				if (!this.isCommunityCropping) {
					headers['Asport-User-Context-ID'] = document.head.querySelector('meta[name="asport-user-context-id"]').content;
				}

				const route = laroute.route(this.isCommunityCropping ? 'api.event.community_video_crop.create' : 'api.event.video_crop.create', { event: this.eventId });
				const response = await fetch(route, {
					method: 'POST',
					body:   JSON.stringify({ event: this.eventId, ...this.clipData }),
					headers,
				});

				const data = await response.json();
				this.cropVideoStart = Date.now();

				if (!response.ok) {
					Sentry.captureMessage('MultiFormatTool: Cropping Backend Error', {
						level: 'fatal',
						tags:  {
							eventId: this.eventId,
							group:   'multi-format-tool',
						},
						extra: {
							errorDetails: data,
							timestamp:    new Date().toISOString(),
						},
					});

					// Throw an error if the response status is not ok (not in the range 200-299)
					throw new Error(`HTTP error! Status: ${response.status}`);
				}

				this.cropVideoId = data.id;
				this.cropVideoStatus = data.status;

				// todo: (AVC) cleanup old status update logic
				if (data.useVc) {
					this.cropTaskWsSubscription = ws.subscribe({
						channel:  `eventVideoCrop.${this.cropVideoId}`,
						event:    WS_EVENT.EVENT_VIDEO_CROP_UPDATED,
						callback: this.handleCropUpdated,
					});

					this.cropOvertimeInterval = setInterval(() => {
						// Video takes longer to process than expected
						const timeElapsed = Date.now() - this.cropVideoStart;
						if (timeElapsed > CROP_VIDEO_PROCESSING_OVERTIME) {
							this.internalUiStatus = UI_STATUS.VIDEO_CROP_OVERTIME;
							clearInterval(this.cropOvertimeInterval);
						}
					}, 1000);
				} else {
					this.cropVideoDataUrl = window.oldCropDataUrl
						.replace('{avsVideoId}', data.avsVideoId)
						.replace('{avsVideoCropId}', data.avsVideoCropId);

					this.checkVideoStatusInterval = setInterval(() => this.getVideoCropStatus(), API_POLLING_INTERVAL_MS);
				}
			} catch (error) {
				this.internalUiStatus = UI_STATUS.VIDEO_CROP_FAILURE;
				this.$toast.error(this.$I18n.trans('commons.something_went_wrong'));

				Sentry.captureMessage('MultiFormatTool: Cropping Frontend Error', {
					level: 'fatal',
					tags:  {
						eventId: this.eventId,
						group:   'multi-format-tool',
					},
					extra: {
						errorDetails: error,
						timestamp:    new Date().toISOString(),
					},
				});

				console.error(error);
			}
		},
		async storeEmailNotification() {
			this.emailNotificationSaved = false;
			this.emailNotificationError = false;
			try {
				const response = await api.createVideoCropNotification(this.cropVideoId, this.email);
				const data = response.data;

				if (data && data.status === 'ok') {
					this.emailNotificationSaved = true;
					this.internalUiStatus = UI_STATUS.EMAIL_NOTIFICATION_REQUESTED;

					if (this.checkVideoStatusInterval) {
						clearInterval(this.checkVideoStatusInterval);
					}
					if (this.cropTaskWsSubscription) {
						ws.unsubscribe(this.cropTaskWsSubscription);
					}
				} else {
					throw new Error('Unable to store email notification: ' + data);
				}
			} catch (e) {
				this.emailNotificationError = true;
			}
		},
		async handleCropUpdated(update) {
			// todo: (AVC) workaround because processing means we can serve the file from the avc. old processing should be building now
			let newStatus = update.eventVideoCrop.status;
			if (newStatus === VIDEO_CROP_STATUS.PROCESSING) {
				newStatus = VIDEO_CROP_STATUS.AVAILABLE;
			}

			this.cropVideoStatus = newStatus;

			// Get a shortlink for the video crop
			if (this.cropVideoStatus === VIDEO_CROP_STATUS.AVAILABLE) {
				this.cropVideoDownloadUrl = await getShortLinkForMoment(this.cropVideoId);
			}

			// Keep ws connection open until failed or processed
			if ([VIDEO_CROP_STATUS.AVAILABLE, VIDEO_CROP_STATUS.FAILED].includes(this.cropVideoStatus)) {
				ws.unsubscribe(this.cropTaskWsSubscription);
			}
		},
		async prefetchVideoForSharing() {
			if (!this.isCommunityCropping || !this.isFileSharingSupported) {
				return;
			}

			try {
				const response = await fetch(this.cropVideoDownloadUrl, {
					credentials: 'omit',
				});
				this.videoBlob = await response.blob();
				this.videoReadyForSharing = true;
			} catch (error) {
				console.error('Error pre-fetching video:', error);
				this.videoReadyForSharing = false;
			}
		},
		async shareVideo() {
			if (!this.isCommunityCropping || !this.videoReadyForSharing || !this.videoBlob) {
				return;
			}

			try {
				const file = new File([this.videoBlob], 'video.mp4', { type: 'video/mp4' });
				await navigator.share({
					files: [file],
				});
			} catch (error) {
				if (error.name !== 'AbortError') {
					this.$toast.error(this.$I18n.trans('commons.something_went_wrong'));
					console.error('Error sharing video:', error);
				}
			}
		},
		setMaxDuration(durationS) {
			const durationMs = durationS * 1000;
			this.maxClipDuration = Math.floor(Math.min(durationMs, CLIP_MAX_DURATION_MS) / 1000) * 1000;
		},
		handleTabChange(tabKey) {
			this.activeTab = tabKey;
		},
		openHelp() {
			window.eventBus.$emit(`modal:${this.modalNameKeyboardShortcuts}`, KEYBOARD_SHORTCUTS);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.multi-format-tool {
	display: flex;
	flex-direction: column;
	height: 100%;
}

.multi-format-tool__header {
	display: flex;
	height: 52px;
	flex-shrink: 0;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
	padding: 0 $sp4;

	@include size(md-up) {
		height: 70px;
	}
}

.multi-format-tool__title {
	display: flex;
	flex-direction: row;
	align-items: center;
	padding-right: 24px;
	padding-top: 0.6em;
	cursor: pointer;
	font-size: 18px; // stylelint-disable-line sh-waqar/declaration-use-variable, property-disallowed-list
	line-height: 1; // stylelint-disable-line sh-waqar/declaration-use-variable, property-disallowed-list
	margin-bottom: $sp5;

	span { // stylelint-disable-line selector-max-type
		position: relative;

		&::after { // stylelint-disable-line selector-max-type
			content: 'spotlight';
			position: absolute;
			right: -5%;
			top: 105%;
			font-size: 64%; // stylelint-disable-line sh-waqar/declaration-use-variable, property-disallowed-list
			color: var(--color-grey-200);

			body.-t-light & { // stylelint-disable-line
				color: var(--color-grey-600);
			}
		}
	}

	@include size(md-up) {
		font-size: 20px; // stylelint-disable-line sh-waqar/declaration-use-variable, property-disallowed-list
	}
}

.multi-format-tool__main-content {
	position: relative;
	display: flex;
	flex-grow: 1;
	flex-direction: column;
	justify-content: center;
	gap: $sp2;
	overflow: hidden; // enable scrolling panels

	@include size(md-up) {
		flex-direction: row;
	}
}

.multi-format-tool__col {
	display: flex;
	flex-direction: column;
	gap: $sp2;
	height: 100%;

	&.-main {
		flex-grow: 1;

		.multi-format-tool.is-mobile & {
			height: auto;
			flex-grow: 0;
			flex-shrink: 0;
		}
	}

	&.-right {
		width: 100%;

		@include size(md-up) {
			width: 475px;
			flex-shrink: 0;
		}
	}
}

.multi-format-tool__loading-spinner {
	width: 100%;
	margin: 0 auto;
	display: flex;
	justify-content: center;
	align-items: center;
	height: 100%;
}

.multi-format-tool__tool-w {
	@include mobile-portrait {
		padding: 0;
	}
}

.multi-format-tool__actions-w {
	min-height: 140px;
	display: flex;
	flex-direction: row;
	align-items: center;
}

// .multi-format-tool__clips-w {}

.multi-format-tool__main-content {
	display: flex;
	justify-content: center;
}

.multi-format-tool__player-w {
	will-change: transform;
	width: 100%;
	max-width: 1200px;
}

.multi-format-tool__preview-container {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: var(--one-panel-background-color);
	mask-size: 100% 100%;
	mask-position: center;
	mask-repeat: no-repeat;
}

.multi-format-tool__in-progress-info {
	text-align: center;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: $sp5;
}

.multi-format-tool__preview-graphic-container {
	position: absolute;
	bottom: 0;
	left: 50%;
	transform: translateX(-50%);
	right: 0;
	top: 0;
}

.multi-format-tool__preview-graphic {
	height: auto;
	position: absolute;
}

.multi-format-tool__overtime-message {
	text-align: left;
}

.multi-format-tool__overtime-email {
	margin-top: $sp4;
	margin-bottom: 0;
}

.multi-format-tool__notification-message {
	display: flex;
	align-items: flex-start;
	flex-direction: row;
	text-align: left;

	.v2-icon {
		margin-right: $sp4;
	}
}

.multi-format-tool__copy-link {
	margin-top: $sp6;

	&::v-deep .v2-input-text {
		margin-bottom: 0;
	}
}

.multi-format-tool__tab-nav {
	flex-grow: 1;
	overflow: hidden;
}

.multi-format-tool__controls {
	padding: $sp5 $sp4;
}

.multi-format-tool__clips {
	overflow: hidden;
}

.multi-format-tool__rotate-message {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: $sp4;
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	text-align: center;
	font-size: 24px; // stylelint-disable-line
	color: var(--color-white);
}

.multi-format-tool__help-button {
	font-size: 24px; // stylelint-disable-line
	font-weight: 300; // stylelint-disable-line
}

</style>
